import { useRef, useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { AppShell } from '../components/AppShell'
import { StudentForm, blankStudent } from '../components/StudentForm'
import { useEntityList, useEntitySave, makeEntity, now } from '../api/hooks/useEntity'
import type { SchoolClass, Student } from '../types/entities'
import { ENTITY } from '../types/entities'
import { parseCsv, toCsv, downloadFile } from '../utils/csv'
import { localIsoDate } from '../utils/dates'
import { accessLogApi } from '../api/accessLog'

/** Список всех учеников учителя с фильтром по классу и поиском по ФИО. */
export function StudentsPage() {
  const { t } = useTranslation()
  const classes = useEntityList<SchoolClass>(ENTITY.schoolClass)
  const students = useEntityList<Student>(ENTITY.student)
  const save = useEntitySave<Student>(ENTITY.student)
  const [classFilter, setClassFilter] = useState<string>('')
  const [search, setSearch] = useState('')
  const [editing, setEditing] = useState<Student | null>(null)
  const [importMsg, setImportMsg] = useState<string | null>(null)
  const fileInput = useRef<HTMLInputElement>(null)

  const classMap = useMemo(() => {
    const m = new Map<string, SchoolClass>()
    for (const c of classes.data ?? []) m.set(c.id, c)
    return m
  }, [classes.data])

  const filteredStudents = useMemo(() => {
    const q = search.trim().toLowerCase()
    return (students.data ?? [])
      .filter((s) => !classFilter || s.classId === classFilter)
      .filter((s) => {
        if (!q) return true
        const fio = `${s.lastName} ${s.firstName} ${s.middleName ?? ''}`.toLowerCase()
        return fio.includes(q)
      })
      .sort(
        (a, b) =>
          a.lastName.localeCompare(b.lastName) ||
          a.firstName.localeCompare(b.firstName),
      )
  }, [students.data, classFilter, search])

  const activeClasses = (classes.data ?? [])
    .filter((c) => c.archivedAt == null)
    .sort((a, b) => a.gradeLevel - b.gradeLevel || a.name.localeCompare(b.name))

  const handleExport = () => {
    const rows: (string | number | null | undefined)[][] = [
      ['lastName', 'firstName', 'middleName', 'class', 'gender', 'birthDate', 'medicalGroup', 'specialNeeds', 'notes'],
    ]
    for (const s of filteredStudents) {
      rows.push([
        s.lastName,
        s.firstName,
        s.middleName,
        classMap.get(s.classId)?.name ?? '',
        s.gender,
        s.birthDate,
        s.medicalGroup,
        s.specialNeeds,
        s.notes,
      ])
    }
    downloadFile(`students-${localIsoDate()}.csv`, toCsv(rows))
    // 152-ФЗ: выгрузка ПДн учеников фиксируется в журнале доступа (только id)
    accessLogApi.reportExport(ENTITY.student, filteredStudents.map((s) => s.id), classFilter || undefined)
  }

  const handleImport = async (file: File) => {
    setImportMsg(null)
    if (!classFilter) {
      setImportMsg(t('studentsPage.importSelectClassFirst'))
      return
    }
    const text = await file.text()
    const rows = parseCsv(text)
    if (rows.length < 2) {
      setImportMsg(t('studentsPage.importEmptyFile'))
      return
    }
    const header = rows[0].map((h) => h.trim().toLowerCase())
    const idxLast = findIdx(header, ['lastname', 'фамилия', 'last_name'])
    const idxFirst = findIdx(header, ['firstname', 'имя', 'first_name'])
    const idxMiddle = findIdx(header, ['middlename', 'отчество', 'middle_name'])
    const idxGender = findIdx(header, ['gender', 'пол'])
    const idxBirth = findIdx(header, ['birthdate', 'birth_date', 'дата рождения', 'дата'])
    if (idxLast < 0 || idxFirst < 0) {
      setImportMsg(t('studentsPage.importColumnsNotFound'))
      return
    }
    const toCreate: Student[] = []
    for (let i = 1; i < rows.length; i++) {
      const r = rows[i]
      const last = r[idxLast]?.trim()
      const first = r[idxFirst]?.trim()
      if (!last || !first) continue
      toCreate.push(
        makeEntity<Student>({
          ...blankStudent(classFilter),
          lastName: last,
          firstName: first,
          middleName: idxMiddle >= 0 ? r[idxMiddle]?.trim() || null : null,
          gender: idxGender >= 0 ? normalizeGender(r[idxGender]) : null,
          birthDate: idxBirth >= 0 ? normalizeDate(r[idxBirth]) : null,
          createdAt: now(),
          updatedAt: now(),
        }),
      )
    }
    if (toCreate.length === 0) {
      setImportMsg(t('studentsPage.importNoRows'))
      return
    }
    await save.mutateAsync(toCreate)
    setImportMsg(t('studentsPage.importDone', { count: toCreate.length }))
    setTimeout(() => setImportMsg(null), 5000)
  }

  return (
    <AppShell
      title={t('studentsPage.title')}
      subtitle={`${t('studentsPage.subtitleTotal', { count: filteredStudents.length })}${
        classFilter ? t('studentsPage.subtitleClass', { name: classMap.get(classFilter)?.name ?? '' }) : ''
      }`}
      actions={
        <div className="flex gap-2 flex-wrap">
          <input
            ref={fileInput}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0]
              if (f) handleImport(f)
              e.target.value = ''
            }}
          />
          <a
            href="/app/import"
            className={`btn-outline text-sm ${activeClasses.length === 0 ? 'pointer-events-none opacity-50' : ''}`}
            title={t('studentsPage.importTitle')}
          >
            {t('studentsPage.importButton')}
          </a>
          <button
            onClick={handleExport}
            disabled={filteredStudents.length === 0}
            className="btn-outline text-sm"
          >
            {t('studentsPage.exportCsv')}
          </button>
          <button
            onClick={() => setEditing(blankStudent(classFilter))}
            disabled={activeClasses.length === 0}
            className="btn-primary text-sm"
            title={activeClasses.length === 0 ? t('studentsPage.createClassFirst') : undefined}
          >
            {t('studentsPage.addButton')}
          </button>
        </div>
      }
    >
      {importMsg && (
        <div className={`mb-4 px-4 py-2 rounded-lg text-sm ${importMsg.startsWith('✓') ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-yellow-50 text-yellow-800 border border-yellow-200'}`}>
          {importMsg}
        </div>
      )}
      {/* Filters */}
      <div className="flex gap-3 mb-5 flex-wrap">
        <select
          className="input-field max-w-[200px]"
          value={classFilter}
          onChange={(e) => setClassFilter(e.target.value)}
        >
          <option value="">{t('studentsPage.allClasses')}</option>
          {activeClasses.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name} ({t('studentsPage.gradeShort', { grade: c.gradeLevel })})
            </option>
          ))}
        </select>
        <input
          type="search"
          placeholder={t('studentsPage.searchPlaceholder')}
          className="input-field flex-1 max-w-[320px]"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {students.isLoading ? (
        <div className="text-center py-12 text-gray-500">{t('common.loading')}</div>
      ) : filteredStudents.length === 0 ? (
        <EmptyState
          hasClasses={activeClasses.length > 0}
          hasFilter={!!classFilter || !!search}
          onAdd={() => setEditing(blankStudent(classFilter))}
        />
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-x-auto">
          <table className="w-full text-sm min-w-[560px]">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-4 py-2 font-semibold text-gray-700">{t('studentsPage.colName')}</th>
                <th className="text-left px-4 py-2 font-semibold text-gray-700 hidden md:table-cell">{t('studentsPage.colClass')}</th>
                <th className="text-left px-4 py-2 font-semibold text-gray-700 hidden lg:table-cell">{t('studentsPage.colGender')}</th>
                <th className="text-left px-4 py-2 font-semibold text-gray-700 hidden lg:table-cell">{t('studentsPage.colBirthDate')}</th>
                <th className="text-left px-4 py-2 font-semibold text-gray-700 hidden md:table-cell">{t('studentsPage.colSpecialNeeds')}</th>
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody>
              {filteredStudents.map((s) => (
                <tr
                  key={s.id}
                  onClick={() => setEditing(s)}
                  className="border-t border-gray-100 hover:bg-primary-soft/30 cursor-pointer transition-colors"
                >
                  <td className="px-4 py-2.5 font-medium text-gray-900">
                    {s.lastName} {s.firstName}
                    {s.middleName && <span className="text-gray-500 ml-1">{s.middleName}</span>}
                    <div className="md:hidden text-xs text-gray-500">
                      {classMap.get(s.classId)?.name ?? '—'}
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-gray-700 hidden md:table-cell">
                    {classMap.get(s.classId)?.name ?? '—'}
                  </td>
                  <td className="px-4 py-2.5 text-gray-700 hidden lg:table-cell">
                    {s.gender === 'M' ? t('studentsPage.genderMale') : s.gender === 'F' ? t('studentsPage.genderFemale') : '—'}
                  </td>
                  <td className="px-4 py-2.5 text-gray-700 hidden lg:table-cell">
                    {s.birthDate ?? '—'}
                  </td>
                  <td className="px-4 py-2.5 text-gray-500 hidden md:table-cell truncate max-w-[200px]">
                    {s.specialNeeds ?? '—'}
                  </td>
                  <td className="text-right pr-3 text-gray-300">›</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && <StudentForm initial={editing} onClose={() => setEditing(null)} />}
    </AppShell>
  )
}

function findIdx(header: string[], names: string[]): number {
  for (const n of names) {
    const i = header.indexOf(n)
    if (i >= 0) return i
  }
  return -1
}

function normalizeGender(v: string | undefined): 'M' | 'F' | null {
  if (!v) return null
  const s = v.trim().toLowerCase()
  const M = ['m', 'м', 'муж', 'мужской', 'мальчик', 'male']
  const F = ['f', 'ж', 'жен', 'женский', 'девочка', 'female']
  if (M.indexOf(s) >= 0) return 'M'
  if (F.indexOf(s) >= 0) return 'F'
  return null
}

function normalizeDate(v: string | undefined): string | null {
  if (!v) return null
  const s = v.trim()
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s
  const m = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/.exec(s)
  if (m) return m[3] + '-' + m[2].padStart(2, '0') + '-' + m[1].padStart(2, '0')
  return null
}

function EmptyState({
  hasClasses,
  hasFilter,
  onAdd,
}: {
  hasClasses: boolean
  hasFilter: boolean
  onAdd: () => void
}) {
  const { t } = useTranslation()
  if (!hasClasses) {
    return (
      <div className="text-center py-12 bg-gradient-to-br from-primary-soft/30 to-white border border-purple-100/80 rounded-2xl">
        <div className="text-5xl mb-3">📘</div>
        <h3 className="font-semibold text-gray-900 mb-2">{t('studentsPage.emptyNoClassesTitle')}</h3>
        <p className="text-sm text-gray-600 mb-4 px-4">
          {t('studentsPage.emptyNoClassesText')}
        </p>
        <a href="/app/classes" className="btn-primary text-sm inline-block">
          {t('studentsPage.goToClasses')}
        </a>
      </div>
    )
  }
  if (hasFilter) {
    return (
      <div className="text-center py-12 text-gray-500">
        {t('studentsPage.emptyNoMatch')}
      </div>
    )
  }
  return (
    <div className="text-center py-12 bg-gradient-to-br from-primary-soft/30 to-white border border-purple-100/80 rounded-2xl">
      <div className="text-5xl mb-3">👥</div>
      <h3 className="font-semibold text-gray-900 mb-2">{t('studentsPage.emptyNoStudentsTitle')}</h3>
      <p className="text-sm text-gray-600 mb-4 px-4">
        {t('studentsPage.emptyNoStudentsText')}
      </p>
      <button onClick={onAdd} className="btn-primary text-sm">
        {t('studentsPage.addFirstStudent')}
      </button>
    </div>
  )
}
